import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label, LabelSection, MasterSourceSelect, Section, Separator, Switch } from '@Components';
import { Input } from '@Components/ui-v2/input';
import { ConfigSupply } from '@Models';

interface SupplySectionProps {
  value: ConfigSupply;
  onChange: (value: ConfigSupply) => void;
  readOnly?: boolean;
}

const SupplySection = ({ value, onChange, readOnly = true }: SupplySectionProps) => {
  const { t } = useTranslation();
  const [useOrderFrequency, setUseOrderFrequency] = useState(value.OrderFrequency?.MasterSource !== undefined);

  const handleLeadTimesVariabilityChange = (variability: number) => {
    onChange({
      ...value,
      LeadTimes: {
        ...value.LeadTimes,
        Variability: variability,
      },
    });
  };

  const handleOrderFrequencyToggle = (enabled: boolean) => {
    setUseOrderFrequency(enabled);
    if (!enabled) {
      // Remove order frequency from the config when disabled
      onChange({
        ...value,
        OrderFrequency: undefined,
      });
    } else {
      // Set default order frequency when enabled
      onChange({
        ...value,
        OrderFrequency: {
          MasterSource: null,
          MasterColumnName: undefined,
          DefaultValue: 7,
        },
      });
    }
  };

  const handleOrderFrequencyChange = (orderFrequency: typeof value.OrderFrequency) => {
    if (readOnly) return;
    onChange({
      ...value,
      OrderFrequency: orderFrequency,
    });
  };

  return (
    <div className="space-y-6 mt-10">
      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Supply.LeadTimes')}
            description={t('ConfigurationPolicy.Supply.LeadTimesDesc')}
          />
        }
        right={
          <div className="space-y-6">
            <MasterSourceSelect
              value={{
                MasterSource: value.LeadTimes.MasterSource,
                MasterColumnName: value.LeadTimes.MasterColumnName,
                DefaultValue: value.LeadTimes.DefaultValue,
              }}
              readOnly={readOnly}
              onChange={(newValue) => {
                if (readOnly) return;
                onChange({
                  ...value,
                  LeadTimes: {
                    ...newValue,
                    Variability: value.LeadTimes.Variability,
                  },
                });
              }}
              defaultLabel={t('ConfigurationPolicy.Supply.DefaultLeadTimeDays')}
            />
          </div>
        }
      />

      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Supply.LeadTimesVariability')}
            description="Additional variation in lead times to account for supply chain uncertainty"
          />
        }
        right={
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label>{t('ConfigurationPolicy.Supply.LeadTimesVariabilityDays')}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={50}
                value={value.LeadTimes.Variability ?? 0}
                onChange={(e) => !readOnly && handleLeadTimesVariabilityChange(Number(e.target.value))}
                readOnly={readOnly}
                className="bg-white"
              />
            </div>
          </div>
        }
      />

      <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

      <Section
        left={
          <LabelSection
            label={t('ConfigurationPolicy.Supply.OrderFrequency')}
            description={t('ConfigurationPolicy.Supply.OrderFrequencyDesc')}
          />
        }
        right={
          <div className="flex items-center justify-end">
            <Switch
              id="use-order-frequency"
              checked={useOrderFrequency}
              onCheckedChange={handleOrderFrequencyToggle}
              disabled={readOnly}
            />
          </div>
        }
      />

      {useOrderFrequency && (
        <>
          <Separator orientation="horizontal" className="w-full bg-gray-300 h-[1px]" />

          <Section
            left={
              <LabelSection
                label={t('ConfigurationPolicy.Supply.DefaultValue', {
                  type: t('ConfigurationPolicy.Supply.OrderFrequency'),
                })}
                description=""
              />
            }
            right={
              <div className="space-y-3">
                <MasterSourceSelect
                  value={{
                    MasterSource: value.OrderFrequency?.MasterSource || null,
                    MasterColumnName: value.OrderFrequency?.MasterColumnName,
                    DefaultValue: value.OrderFrequency?.DefaultValue || 7,
                  }}
                  readOnly={readOnly}
                  onChange={handleOrderFrequencyChange}
                  defaultLabel={t('ConfigurationPolicy.Supply.DefaultValue', {
                    type: t('ConfigurationPolicy.Supply.OrderFrequency'),
                  })}
                />
              </div>
            }
          />
        </>
      )}
    </div>
  );
};

export default SupplySection;
